/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Pricing } from '../../../../../Components/UIElements/Pricing';
import { previewData } from '../../../../../Signals/formStateSignal';

/**
 * ContractPreview Component
 * @returns {JSX.Element}
 * @constructor
 */
export function ContractPreview() {
  const { t } = useTranslation();
  const {
    values: { rateBundleTerm },
  } = useFormikContext();

  return (
    <Grid container mt={4}>
      <Grid item xs={12}>
        <Typography
          sx={{
            fontWeight: 'bold',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: '#49ad33',
          }}
        >
          {t('contractPreviewHeader')}
        </Typography>
        <Box
          mt={2}
          sx={{
            backgroundColor: 'rgba(73,173,51,0.2)',
            padding: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{t('duration')}</span>
          <span>
            {rateBundleTerm?.termValue > 1
              ? `${rateBundleTerm?.termValue} ${t('months')}`
              : `${rateBundleTerm?.termValue} ${t('month')}`}
          </span>
        </Box>
        <Box
          sx={{
            backgroundColor: 'rgba(73,173,51,0.1)',
            padding: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{t('paymentMethod')}</span>
          <Box>
            {previewData.value?.discountedBasePrice ? (
              <Pricing price={Number(previewData.value.discountedBasePrice)} />
            ) : null}
            <Pricing
              sx={{ marginLeft: '12px' }}
              strikeThrough={!!previewData.value?.discountedBasePrice}
              price={Number(rateBundleTerm?.price)}
            />
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: 'rgba(73,173,51,0.2)',
            padding: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{t('periodOfNotice')}</span>
          <span>
            {rateBundleTerm.cancellationPeriod > 1
              ? `${rateBundleTerm?.cancellationPeriod} ${t('months')}`
              : `${rateBundleTerm?.cancellationPeriod} ${t('month')}`}
          </span>
        </Box>
        <Box
          sx={{
            backgroundColor: 'rgba(73,173,51,0.1)',
            padding: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{t('extensionFixedTerm')}</span>
          <span>
            {rateBundleTerm.extensionFixedTerm > 1
              ? `${rateBundleTerm?.extensionFixedTerm} ${t('months')}`
              : `${rateBundleTerm?.extensionFixedTerm} ${t('month')}`}
          </span>
        </Box>
      </Grid>
    </Grid>
  );
}
